import { fmtClock } from "./date";
import { cueHi, hiCue, tr } from "./exerciseHi";
import { prepSecs, stepSecs } from "./queue";
import type { Exercise, KayaState, QueueStep, RestStep, TimeStep, TrainingProfile, WorkStep } from "./types";

/**
 * The workout player's decisions that are not layout (ADR-055): which mode this phone runs, the
 * stats line, and every word the Simple player shows, so they are testable without a renderer.
 * The page owns the clock and the store; `PlayerShell` owns the layout; this owns the words.
 */

export type PlayerLang = "en" | "hi";

/** `LocalMeta`'s three player fields; this module cannot import storage. */
export interface PlayerPrefsIn {
  simplePlayer?: boolean;
  voice?: boolean;
  lang?: PlayerLang;
}

export interface PlayerMode {
  simple: boolean;
  voice: boolean;
  lang: PlayerLang;
}

/**
 * The phone's own choice wins; without one, what a linked owner set in `profile.training` (or what
 * the questionnaire answered); without either, the Normal player, silent, in English.
 */
export function playerMode(training: TrainingProfile | undefined, local: PlayerPrefsIn): PlayerMode {
  return {
    simple: local.simplePlayer ?? training?.simple ?? false,
    voice: local.voice ?? training?.voice ?? false,
    lang: local.lang ?? training?.lang ?? "en",
  };
}

/* ---------- figures ---------- */

/** Work steps before `i`: what "moves done" counts. */
export function movesDone(queue: readonly QueueStep[], i: number): number {
  return queue.slice(0, Math.max(0, i)).filter((q) => q.kind !== "rest").length;
}

/**
 * Whole minutes left from step `i` on, rounded up so it never says 0 while a move is left: each
 * step at `stepSecs`, the get-set before every timed move, and the current step at what its clock
 * says when it is running.
 */
export function minutesLeft(S: KayaState, queue: readonly QueueStep[], i: number, simple: boolean, currentLeftSecs?: number): number {
  const lead = prepSecs(S, simple);
  let secs = 0;
  for (let j = Math.max(0, i); j < queue.length; j++) {
    const st = queue[j];
    if (j === i && currentLeftSecs !== undefined) secs += Math.max(0, currentLeftSecs);
    else secs += stepSecs(st) + (st.kind === "time" ? lead : 0);
  }
  return Math.ceil(secs / 60);
}

/** One figure under the ring, in `PlayerShell`'s shape. */
export interface ViewStat {
  figure: string | number | null;
  label: string;
  estimated?: boolean;
}

export interface LiveStatsIn {
  simple: boolean;
  /** a Steady person: never kcal (plan §5; Progress already holds this, ADR-054) */
  steady: boolean;
  lang: PlayerLang;
  screen: "move" | "rest";
  /** the work step on screen, counted from 1 (on a rest, the work steps up to it) */
  stepNo: number;
  total: number;
  done: number;
  round: { round: number; rounds: number } | null;
  kcal: number;
  minsLeft: number;
}

/**
 * The stats line. Normal keeps its three — the move or moves done, the round, kcal — except that a
 * Steady person sees minutes left where kcal was. Simple has two: moves done and minutes left.
 */
export function liveStats(o: LiveStatsIn): ViewStat[] {
  if (o.simple) {
    return [
      { figure: `${o.done}/${o.total}`, label: tr(o.lang, "moves done") },
      { figure: o.minsLeft, label: tr(o.lang, "minutes left") },
    ];
  }
  const last: ViewStat = o.steady
    ? { figure: o.minsLeft, label: "minutes left" }
    : { figure: o.kcal, label: "kcal", estimated: true };
  const round = o.round ? [{ figure: `${o.round.round}/${o.round.rounds}`, label: "round" }] : [];
  return o.screen === "move"
    ? [{ figure: `${o.stepNo}/${o.total}`, label: "move" }, ...round, last]
    : [{ figure: `${o.stepNo}/${o.total}`, label: "moves done" }, ...round, last];
}

export interface FinishedStatsIn {
  simple: boolean;
  steady: boolean;
  lang: PlayerLang;
  mins: number;
  kcal: number;
  streak: number;
  moves: number;
}

/**
 * The finished screen. Normal: active minutes, kcal and the streak, with the streak's line. A
 * Steady person or the Simple player: minutes and moves, and no streak — a missed day for a
 * family function is not something to lose (delivery-for-parents.md §6, French et al. 2014).
 */
export function finishedStats(o: FinishedStatsIn): { stats: ViewStat[]; streakLine: boolean } {
  if (o.simple || o.steady) {
    return {
      stats: [
        { figure: o.mins, label: tr(o.lang, "active min") },
        { figure: o.moves, label: tr(o.lang, "moves") },
      ],
      streakLine: false,
    };
  }
  return {
    stats: [
      { figure: o.mins, label: "active min" },
      { figure: o.kcal, label: "kcal", estimated: true },
      { figure: o.streak, label: "day streak" },
    ],
    streakLine: true,
  };
}

/* ---------- the Simple player's words ---------- */

export type CtlId = "stop" | "pause" | "resume" | "done" | "next" | "skipRest" | "redo";

export interface Ctl {
  id: CtlId;
  label: string;
}

export type SimpleStage = "ready" | "set" | "work" | "workdone" | "rest";

/**
 * Three controls, always: Stop in words on the left, the 96 px centre, and the right. A reps move
 * puts Done in the centre (the one thing to press) and Next on the right; a timed move and a rest
 * put Pause in the centre, because they go on by themselves.
 */
export function simpleControls(stage: SimpleStage, kind: QueueStep["kind"], paused: boolean, lang: PlayerLang): { left: Ctl; centre: Ctl; right: Ctl } {
  const stop: Ctl = { id: "stop", label: tr(lang, "Stop") };
  const pause: Ctl = paused ? { id: "resume", label: tr(lang, "Resume") } : { id: "pause", label: tr(lang, "Pause") };
  const next: Ctl = { id: "next", label: tr(lang, "Next") };
  if (kind === "rest") return { left: stop, centre: pause, right: { id: "skipRest", label: tr(lang, "Skip rest") } };
  if (kind === "reps") return { left: stop, centre: { id: "done", label: tr(lang, "Done") }, right: next };
  if (stage === "workdone") return { left: stop, centre: { id: "redo", label: tr(lang, "Start") }, right: next };
  return { left: stop, centre: pause, right: next };
}

/** A move's name in the player's language, and the other language's under it (board
 *  PlayerSimpleWork: the Hindi under the English; PlayerSimpleHindi: the English under the Hindi). */
export function moveNames(ex: Exercise, lang: PlayerLang): { name: string; nameLang: PlayerLang; sub: string | null; subLang: PlayerLang } {
  if (lang === "hi" && ex.hi) return { name: ex.hi, nameLang: "hi", sub: ex.n, subLang: "en" };
  return { name: ex.n, nameLang: "en", sub: ex.hi ?? null, subLang: "hi" };
}

/** The opening of a move's `how` as one line: its first sentence, or two when the first is only
 *  the set-up ("Sit tall.") and says nothing about the move. */
export function leadCue(how: string): string {
  const parts = how.split(". ");
  let s = parts[0];
  if (s.length < 30 && parts.length > 1) s = `${s}. ${parts[1]}`;
  return s.endsWith(".") ? s : `${s}.`;
}

/** The step's filled cue ("hands allowed") in the language, phrase by phrase. */
export function stepCue(st: WorkStep, lang: PlayerLang): string | null {
  if (!st.cue) return null;
  return lang === "hi" ? cueHi(st.cue) : st.cue;
}

function doseWords(st: QueueStep, lang: PlayerLang): string {
  if (st.kind === "rest" || st.kind === "time") return lang === "hi" ? `${st.secs} ${tr(lang, "seconds")}` : `${st.secs} s`;
  const each = st.side ? (lang === "hi" ? ` · ${tr(lang, "each side")}` : " each side") : "";
  return `${st.reps} ${tr(lang, "reps")}${each}`;
}

/**
 * "Next · Wall push-ups · 5 reps": the step after `i`. A labelled rest is named when it comes next
 * (board PlayerSimpleWork: "Next · Stand up slowly · 30 s"), because it asks something of the
 * body; a plain rest is skipped over.
 */
export function simpleNext(queue: readonly QueueStep[], i: number, lang: PlayerLang): string | null {
  for (let j = i + 1; j < queue.length; j++) {
    const st = queue[j];
    if (st.kind === "rest") {
      if (st.label) return `${tr(lang, "Next")} · ${tr(lang, st.label)} · ${doseWords(st, lang)}`;
      continue;
    }
    const n = moveNames(st.ex, lang).name;
    const side = st.kind === "time" && st.sideLabel ? ` (${tr(lang, st.sideLabel)})` : "";
    return `${tr(lang, "Next")} · ${n}${side} · ${doseWords(st, lang)}`;
  }
  return null;
}

export interface SimpleView {
  title: string;
  name: string;
  nameLang: PlayerLang;
  sub: string | null;
  subLang: PlayerLang;
  cue: string | null;
  cueLang: PlayerLang;
  /** the rest's "Breathe" / "Stand up slowly" at the large size */
  large: boolean;
  ring: { pct: number; figure: string | number; unit: string; label: string };
  next: string | null;
  left: Ctl;
  centre: Ctl;
  right: Ctl;
  more: string;
  menu: { id: "prev" | "how" | "sound"; label: string }[];
  on: string;
  off: string;
}

export interface SimpleIn {
  lang: PlayerLang;
  /** the session's English name */
  title: string;
  queue: readonly QueueStep[];
  i: number;
  stage: SimpleStage;
  paused: boolean;
  /** the running clock, when the stage has one */
  remainingMs: number;
  totalSecs: number;
}

/** Everything the Simple player says on a move or a rest. */
export function simpleView(o: SimpleIn): SimpleView {
  const { lang } = o;
  const st = o.queue[o.i];
  const ctl = simpleControls(o.stage, st.kind, o.paused, lang);
  const common = {
    title: tr(lang, o.title),
    next: simpleNext(o.queue, o.i, lang),
    ...ctl,
    more: tr(lang, "More"),
    menu: [
      { id: "prev" as const, label: tr(lang, "Previous") },
      { id: "how" as const, label: tr(lang, "How to do this move") },
      { id: "sound" as const, label: tr(lang, "Sound") },
    ],
    on: tr(lang, "On"),
    off: tr(lang, "Off"),
  };
  const paused = o.paused ? ` · ${tr(lang, "paused")}` : "";

  if (st.kind === "rest") return { ...common, ...restWords(st, lang, o, paused) };

  const names = moveNames(st.ex, lang);
  const side = st.kind === "time" && st.sideLabel ? ` · ${tr(lang, st.sideLabel)}` : "";
  const hiHow = lang === "hi" ? hiCue(st.ex.id) : null;
  const how = hiHow ?? leadCue(st.ex.how);
  const cueLang: PlayerLang = hiHow ? "hi" : "en";
  const filled = stepCue(st, lang);

  if (st.kind === "reps") {
    const each = st.side ? ` · ${tr(lang, "each side")}` : "";
    const unit = `${tr(lang, "reps")}${each}${filled ? ` · ${filled}` : ""}${paused}`;
    return {
      ...common,
      ...names,
      name: names.name,
      cue: how,
      cueLang,
      large: false,
      ring: { pct: 1, figure: st.reps, unit, label: `${st.reps} ${tr(lang, "reps")}${each}${filled ? `, ${filled}` : ""}` },
    };
  }

  const t = st as TimeStep;
  const cue = filled ? `${how} ${filled.charAt(0).toUpperCase()}${filled.slice(1)}.` : how;
  const total = fmtClock(t.secs * 1000);
  let ring: SimpleView["ring"];
  if (o.stage === "set") {
    const n = Math.ceil(o.remainingMs / 1000);
    ring = { pct: frac(o), figure: n, unit: `${tr(lang, "get set")}${paused}`, label: `${n} ${tr(lang, "seconds")}, ${tr(lang, "get set")}` };
  } else if (o.stage === "work") {
    const left = fmtClock(o.remainingMs);
    ring = { pct: frac(o), figure: left, unit: `${lang === "hi" ? `${total} में से` : `left of ${total}`}${paused}`, label: `${left} / ${total}` };
  } else {
    ring = { pct: o.stage === "workdone" ? 1 : 0, figure: total, unit: `${t.secs} ${tr(lang, "seconds")}${paused}`, label: `${t.secs} ${tr(lang, "seconds")}` };
  }
  return { ...common, ...names, name: `${names.name}${side}`, cue, cueLang, large: false, ring };
}

function frac(o: SimpleIn): number {
  return o.totalSecs ? 1 - o.remainingMs / (o.totalSecs * 1000) : 0;
}

function restWords(st: RestStep, lang: PlayerLang, o: SimpleIn, paused: string) {
  const left = fmtClock(o.remainingMs);
  const of = fmtClock(o.totalSecs * 1000);
  const name = st.label ? tr(lang, st.label) : tr(lang, "Breathe");
  const cue = st.label
    ? tr(lang, "Sit a moment, then stand with a hand on the counter until you feel steady.")
    : tr(lang, "Sit down and breathe.");
  return {
    name,
    nameLang: lang,
    sub: null,
    subLang: lang,
    cue,
    cueLang: lang,
    large: true,
    ring: {
      pct: frac(o),
      figure: left,
      unit: `${lang === "hi" ? `${tr(lang, "rest left")} · ${of}` : `rest left of ${of}`}${paused}`,
      label: `${left} ${tr(lang, "rest left")}`,
    },
  };
}

/** The Simple player's start screen. */
export function simpleIdle(lang: PlayerLang, title: string, minutes: number, moves: number) {
  return {
    title: tr(lang, title),
    unit: `${tr(lang, "minutes")} · ${moves} ${tr(lang, "moves")}`,
    cue: tr(lang, "Timed moves and rests go on by themselves. Tap Done when your reps are finished."),
    start: tr(lang, "Start"),
    label: `${minutes} ${tr(lang, "minutes")}, ${moves} ${tr(lang, "moves")}`,
  };
}

/* ---------- what the voice says ---------- */

/**
 * You › Training's honest line about the clips (board YouTrainingVoice): how many this phone has,
 * and with none, what speaks instead — the phone's own voice, or beeps only.
 */
export function voiceClipsLine(clips: number, phoneVoice: boolean): string {
  if (clips > 0) return `${clips} on this phone`;
  return `None on this phone yet · ${phoneVoice ? "using the phone’s voice" : "beeps only"}`;
}

/**
 * The cue to speak on arriving at step `i`, as voice ids (`voiceWords.ts` words and move ids), or
 * null for nothing. A labelled rest says "stand up slowly"; a plain rest "rest"; the second side of
 * a timed move "switch sides"; a move "next" and its name — "last one" for the last — and the
 * session's first move opens with "stop if it hurts".
 */
export function voiceCue(queue: readonly QueueStep[], i: number): string[] | null {
  const st = queue[i];
  if (!st) return null;
  if (st.kind === "rest") return [st.label ? "stand-up-slowly" : "rest"];
  const prev = queue[i - 1];
  if (st.kind === "time" && st.sideLabel && prev && prev.kind === "time" && prev.itemId === st.itemId) return ["switch-sides"];
  const last = !queue.slice(i + 1).some((q) => q.kind !== "rest");
  const first = movesDone(queue, i) === 0;
  return [...(first ? ["stop-if-it-hurts"] : []), last ? "last-one" : "next", st.ex.id];
}

/** Halfway through a timed move of 20 s or more: one word, once. */
export function halfwayCue(secs: number, remainingMs: number): boolean {
  return secs >= 20 && remainingMs <= (secs * 1000) / 2;
}
